import {
  useState,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  forwardRef,
} from "react";
import { useAuth } from "@/features/authentication";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { createToast } from "vercel-toast";
import { sendTrackDataServer } from "@/features/analytics/utils/serverTrackData";
import { Check, Plus, X } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { TicketRoomCard } from "./ticket-room-card";
import { TicketCityField } from "./ticket-city-field";
import { ticketFormCityOptions, useTicketCities } from "./use-ticket-cities";
import { matchHubspotCityOption } from "@/features/vision-360/proposal-standard/hubspotCity";
import { REASON_OPTIONS, DOCUMENT_OPTIONS } from "./constants";
import {
  createTicketFormSchema,
  type TicketFormValues,
} from "./ticket-form.schema";
import { visionService } from "@/services/vision-service";
import type {
  TicketRoom,
  TicketRequestPayload,
  TicketRoomPayload,
} from "./ticket-form.types";

export interface TicketFormHandle {
  /** Valida e envia. Resolve `true` no sucesso; `false` quando há campos
   * pendentes (os erros ficam visíveis e a tela rola até o primeiro). */
  submit: () => Promise<boolean>;
}

interface TicketFormProps {
  rooms: TicketRoom[];
  prefillCity?: string;
  prefillNeighborhoods?: string[];
  prefillPositions?: number | null;
  dealId?: string | number | null;
  dealName?: string | null;
  companyName?: string | null;
  onSuccess?: () => void;
  onValidityChange?: (valid: boolean) => void;
}

const FONT = "'Space Grotesk', system-ui, sans-serif";

const OFFICE_TYPE_ORDER = {
  pronto: 0,
  "personalizado-sob-medida": 1,
  ambos: 2,
} as const;

function parseYesNo(value: string | undefined): boolean | null {
  if (value === "sim") return true;
  if (value === "nao") return false;
  return null;
}

export const TicketForm = forwardRef<TicketFormHandle, TicketFormProps>(
  function TicketForm(
    {
      rooms,
      prefillCity = "",
      prefillNeighborhoods = [],
      prefillPositions,
      dealId,
      dealName,
      companyName,
      onSuccess,
      onValidityChange,
    },
    ref,
  ) {
    const { userInfo } = useAuth();
    const {
      cities,
      isLoading: citiesLoading,
      hasError: citiesError,
      retry: retryCities,
    } = useTicketCities();
    const cityOptions = useMemo(() => ticketFormCityOptions(cities), [cities]);
    // The resolver reads the latest list through a ref: it arrives after the
    // form is created, and useForm keeps the first resolver it gets.
    const cityValuesRef = useRef<string[]>([]);
    cityValuesRef.current = cityOptions.map((option) => option.value);
    // The prefill is a "Cidade - UF" built from the search; only a city that is
    // one of HubSpot's options survives, anything else leaves the field blank
    // for the rep to pick (PED-14049).
    const prefillCityOption = matchHubspotCityOption(prefillCity, cityOptions);
    const {
      control,
      register,
      setValue,
      trigger,
      getValues,
      watch,
      formState: { isValid, errors },
    } = useForm<TicketFormValues>({
      resolver: (values, context, options) =>
        zodResolver(createTicketFormSchema(cityValuesRef.current))(
          values,
          context,
          options,
        ),
      mode: "onChange",
      defaultValues: {
        officeType: "pronto",
        requiresDocs: false,
        selectedDocs: [],
        meetingRoomCapacities: [],
        roomCount: rooms.length,
        positions: prefillPositions ?? undefined,
        // Blank, not undefined: an undefined field would stop zod before the
        // cross-field checks run.
        city: "",
        neighborhoodText: prefillNeighborhoods.join(", ") || undefined,
      },
    });

    const { fields, append, remove } = useFieldArray({
      control,
      name: "meetingRoomCapacities",
    });

    const [roomsState, setRoomsState] = useState<TicketRoom[]>(() =>
      rooms.map((room) => ({ ...room, roomSize: room.sizeM2 ?? "" })),
    );

    // Sync prefill props → form values after async load
    useEffect(() => {
      if (prefillCityOption) {
        setValue("city", prefillCityOption);
        void trigger("city");
      }
    }, [prefillCityOption, setValue, trigger]);

    const neighborhoodsKey = [
      ...new Set([
        ...prefillNeighborhoods,
        ...rooms.map((room) => room.neighborhood).filter(Boolean),
      ]),
    ].join(", ");

    useEffect(() => {
      setValue("neighborhoodText", neighborhoodsKey || undefined);
    }, [neighborhoodsKey, setValue]);

    const officeType = watch("officeType");
    const requiresDocs = watch("requiresDocs");
    const privateMeetingRooms = watch("privateMeetingRooms");
    const reasonOpening = watch("reasonOpening");

    useEffect(() => {
      if (rooms.length === 0 && reasonOpening === "agendamento_visitas") {
        setValue("reasonOpening", "", { shouldValidate: true });
      }
      setValue("roomCount", rooms.length, { shouldValidate: true });
      setRoomsState((prev) => {
        const prevIds = prev.map((r) => r.roomId).join(",");
        const nextIds = rooms.map((r) => r.roomId).join(",");
        if (prevIds === nextIds) return prev;
        return rooms.map((room) => {
          const existing = prev.find((r) => r.roomId === room.roomId);
          return existing
            ? {
                ...room,
                changeRequest: existing.changeRequest,
                roomSize: existing.roomSize,
                roomObservations: existing.roomObservations,
              }
            : { ...room, roomSize: room.sizeM2 ?? "" };
        });
      });
    }, [rooms, setValue, reasonOpening]);

    const officeIndex = OFFICE_TYPE_ORDER[officeType];
    const isCustomOffice =
      officeType === "personalizado-sob-medida" || officeType === "ambos";
    const hasRooms = roomsState.length > 0;
    const isSchedulingReason = reasonOpening === "agendamento_visitas";

    useEffect(() => {
      onValidityChange?.(isValid);
    }, [isValid, onValidityChange]);

    useEffect(() => {
      if (!isSchedulingReason) return;
      const allSet = roomsState.every(
        (r) => r.requestedVisitDate && r.requestedVisitTime,
      );
      setValue("roomVisitsComplete", allSet, { shouldValidate: true });
    }, [roomsState, isSchedulingReason, setValue]);

    useImperativeHandle(ref, () => ({
      async submit() {
        const valid = await trigger();
        if (!valid) {
          // Espera o React pintar as mensagens antes de rolar até a primeira.
          requestAnimationFrame(() => {
            document
              .querySelector("[data-field-error]")
              ?.scrollIntoView({ behavior: "smooth", block: "center" });
          });
          return false;
        }

        const data = getValues();
        const isCustom =
          data.officeType === "personalizado-sob-medida" ||
          data.officeType === "ambos";
        const neighborhoods = (data.neighborhoodText ?? "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);

        const payload: TicketRequestPayload = {
          deal_id: dealId ?? null,
          deal_name: dealName ?? null,
          company_name: companyName ?? null,
          office_type: data.officeType,
          reason: data.reasonOpening,
          city: data.city,
          neighborhoods,
          size_m2: data.size ? Number(data.size) : null,
          positions: data.positions,
          budget: data.budget,
          expected_entry_date: data.expectedEntryDate,
          requires_documentation: data.requiresDocs,
          required_documents: data.requiresDocs
            ? data.selectedDocs.filter((d) => d !== "outro")
            : [],
          required_documents_other:
            data.requiresDocs && data.selectedDocs.includes("outro")
              ? (data.otherDocText ?? null)
              : null,
          observations: data.observations ?? "",
          rooms: roomsState.map(
            (r): TicketRoomPayload => ({
              private_room_id: r.roomId,
              search_id: r.searchId ?? null,
              space_id: r.spaceId,
              room_name: r.roomName,
              space_name: r.spaceName,
              address: r.address,
              change_request: r.changeRequest,
              observations: r.roomObservations,
              requested_visit_date: r.requestedVisitDate,
              requested_visit_time: r.requestedVisitTime,
            }),
          ),
          search_more_options: parseYesNo(data.searchMoreOptions),
          contract_months:
            isCustom && data.contractMonths
              ? Number(data.contractMonths)
              : null,
          open_to_nearby_neighborhoods: isCustom
            ? parseYesNo(data.openToNearby)
            : null,
          private_meeting_rooms: isCustom
            ? parseYesNo(data.privateMeetingRooms)
            : null,
          meeting_room_capacities:
            isCustom && data.privateMeetingRooms === "sim"
              ? data.meetingRoomCapacities
                  .map((r) => r.capacity)
                  .filter((c) => c > 0)
              : [],
          willing_to_adapt_amenities: isCustom
            ? parseYesNo(data.willingToAdapt)
            : null,
          specific_partner_or_restrictions: isCustom
            ? (data.specificPartner ?? "")
            : "",
          visit_requester_email: userInfo?.profile?.email ?? null,
        };

        try {
          await visionService.createTicketRequest(payload);
          sendTrackDataServer({
            eventName: "VISION_TICKET_REQUESTED",
            productType: "PO",
            url: window.location.pathname,
            data: {
              deal_id: dealId ?? null,
              reason: data.reasonOpening,
              office_type: data.officeType,
              city: data.city,
              rooms_count: roomsState.length,
              is_visit_scheduling: data.reasonOpening === "agendamento_visitas",
            },
          }).catch(() => {});
          createToast("Ticket enviado com sucesso!", {
            timeout: 4000,
            type: "success",
          });
          onSuccess?.();
          return true;
        } catch {
          createToast("Erro ao enviar o ticket. Tente novamente.", {
            timeout: 5000,
            type: "error",
          });
          throw new Error("submit failed");
        }
      },
    }));

    const handleRoomFieldChange = (
      index: number,
      field: keyof TicketRoom,
      value: string | number,
    ) => {
      setRoomsState((prev) =>
        prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)),
      );
    };

    return (
      <div className="flex flex-col gap-6" style={{ fontFamily: FONT }}>
        {/* Deal context header */}
        {(companyName || dealId) && (
          <div
            className="rounded-xl px-4 py-3 flex flex-col gap-1"
            style={{ background: "#F0EEEB" }}
          >
            {companyName && (
              <span
                className="text-sm font-semibold leading-tight"
                style={{ color: "#1A1814" }}
              >
                {companyName}
              </span>
            )}
            {dealId && (
              <span className="text-xs" style={{ color: "#78736D" }}>
                Deal ID: {dealId}
              </span>
            )}
          </div>
        )}

        {/* Office type toggle */}
        <div>
          <label
            className="block text-xs font-semibold mb-3 uppercase tracking-wider"
            style={{ color: "#78736D", letterSpacing: "0.08em" }}
          >
            Tipo de escritório
          </label>
          <Controller
            control={control}
            name="officeType"
            render={({ field }) => (
              <div
                className="relative flex rounded-full p-1"
                style={{ background: "#F0EEEB" }}
              >
                <div
                  className="absolute top-1 bottom-1 rounded-full transition-all duration-200 ease-out"
                  style={{
                    background: "#1A1814",
                    width: "calc((100% - 8px) / 3)",
                    left: `calc(4px + ${officeIndex} * (100% - 8px) / 3)`,
                  }}
                />
                {(
                  [
                    { value: "pronto", label: "Escritório Pronto" },
                    {
                      value: "personalizado-sob-medida",
                      label: "Personalizado/ Sob Medida",
                    },
                    { value: "ambos", label: "Ambos" },
                  ] as const
                ).map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    className="relative z-10 flex-1 py-2.5 text-sm font-semibold rounded-full transition-colors duration-200 text-center"
                    style={{
                      color: field.value === value ? "#FFD900" : "#78736D",
                      letterSpacing: "0.02em",
                    }}
                    onClick={() => field.onChange(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          />
        </div>

        {/* Motivo */}
        <div>
          <label
            className="block text-xs font-semibold mb-1.5 uppercase tracking-wider"
            style={{ color: "#78736D", letterSpacing: "0.08em" }}
          >
            Motivo de abertura do ticket{" "}
            <span style={{ color: "#B91C1C" }}>*</span>
          </label>
          <Controller
            control={control}
            name="reasonOpening"
            render={({ field }) => (
              <Select value={field.value ?? ""} onValueChange={field.onChange}>
                <SelectTrigger
                  className="h-10 rounded-xl text-sm"
                  style={{
                    borderColor: "#E8E6E3",
                    color: field.value ? "#43403D" : "#9E9A94",
                  }}
                >
                  <SelectValue placeholder="Selecione o motivo..." />
                </SelectTrigger>
                <SelectContent>
                  {REASON_OPTIONS.filter(
                    (opt) => opt.value !== "agendamento_visitas" || hasRooms,
                  ).map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          <FieldError message={errors.reasonOpening?.message} />
        </div>

        {/* Selected rooms */}
        {hasRooms && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <label
                className="text-xs font-semibold uppercase tracking-wider"
                style={{ color: "#78736D", letterSpacing: "0.08em" }}
              >
                Salas selecionadas
              </label>
              <span
                className="text-xs font-medium px-2.5 py-1 rounded-full"
                style={{ background: "#FFF8D6", color: "#92700C" }}
              >
                {roomsState.length} {roomsState.length === 1 ? "sala" : "salas"}
              </span>
            </div>
            <div className="flex flex-col gap-2">
              {roomsState.map((room, idx) => (
                <TicketRoomCard
                  key={room.roomId}
                  room={room}
                  index={idx}
                  defaultExpanded={roomsState.length <= 3}
                  showVisitScheduling={isSchedulingReason}
                  onFieldChange={handleRoomFieldChange}
                />
              ))}
            </div>
            {isSchedulingReason && (
              <FieldError message={errors.roomVisitsComplete?.message} />
            )}
          </div>
        )}

        {/* Buscar outras opções */}
        {hasRooms && (
          <Controller
            control={control}
            name="searchMoreOptions"
            render={({ field }) => (
              <YesNoField
                label="Buscar outras opções além das anexadas?"
                required
                value={field.value ?? ""}
                onChange={field.onChange}
                error={errors.searchMoreOptions?.message}
              />
            )}
          />
        )}

        <div className="h-px w-full" style={{ background: "#E8E6E3" }} />

        {/* Documentos exigidos */}
        <div>
          <label
            className="block text-xs font-semibold mb-2 uppercase tracking-wider"
            style={{ color: "#78736D", letterSpacing: "0.08em" }}
          >
            Documentos exigidos <span style={{ color: "#B91C1C" }}>*</span>
          </label>
          <Controller
            control={control}
            name="requiresDocs"
            render={({ field }) => (
              <div className="flex gap-2 mb-3">
                {(
                  [
                    { value: true, label: "Exige documentação" },
                    { value: false, label: "Não exige" },
                  ] as const
                ).map(({ value, label }) => {
                  const isActive = field.value === value;
                  return (
                    <button
                      key={String(value)}
                      type="button"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-150"
                      style={{
                        background: isActive ? "#1A1814" : "transparent",
                        color: isActive ? "#FFD900" : "#78736D",
                        border: `1px solid ${isActive ? "#1A1814" : "#E8E6E3"}`,
                      }}
                      onClick={() => {
                        field.onChange(value);
                        setValue("selectedDocs", []);
                        setValue("otherDocText", undefined);
                      }}
                    >
                      {isActive && <Check className="w-3 h-3" />}
                      {label}
                    </button>
                  );
                })}
              </div>
            )}
          />
          {requiresDocs === true && (
            <Controller
              control={control}
              name="selectedDocs"
              render={({ field }) => (
                <div className="flex flex-col gap-3">
                  <div className="flex flex-wrap gap-2">
                    {DOCUMENT_OPTIONS.map((doc) => {
                      const isSelected = field.value.includes(doc.value);
                      return (
                        <button
                          key={doc.value}
                          type="button"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-150"
                          style={{
                            background: isSelected ? "#1A1814" : "transparent",
                            color: isSelected ? "#FFD900" : "#78736D",
                            border: `1px solid ${isSelected ? "#1A1814" : "#E8E6E3"}`,
                          }}
                          onClick={() => {
                            const next = isSelected
                              ? field.value.filter((d) => d !== doc.value)
                              : [...field.value, doc.value];
                            field.onChange(next);
                            if (doc.value === "outro" && isSelected)
                              setValue("otherDocText", undefined);
                          }}
                        >
                          {isSelected && <Check className="w-3 h-3" />}
                          {doc.label}
                        </button>
                      );
                    })}
                  </div>
                  <FieldError message={errors.selectedDocs?.message} />
                  {field.value.includes("outro") && (
                    <input
                      {...register("otherDocText")}
                      type="text"
                      className="w-full h-10 px-3 rounded-xl text-sm outline-none transition-colors"
                      style={{ border: "1px solid #E8E6E3", color: "#43403D" }}
                      placeholder="Descreva o documento exigido"
                      onFocus={(e) => (e.target.style.borderColor = "#FFD900")}
                      onBlur={(e) => (e.target.style.borderColor = "#E8E6E3")}
                    />
                  )}
                  {field.value.includes("outro") && (
                    <FieldError message={errors.otherDocText?.message} />
                  )}
                </div>
              )}
            />
          )}
        </div>

        {/* Metragem + Posições */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label
              className="block text-xs font-semibold mb-1.5 uppercase tracking-wider"
              style={{ color: "#78736D", letterSpacing: "0.08em" }}
            >
              Metragem (m²)
            </label>
            <input
              {...register("size")}
              type="number"
              className="w-full h-10 px-3 rounded-xl text-sm outline-none transition-colors"
              style={{ border: "1px solid #E8E6E3", color: "#43403D" }}
              placeholder="Ex: 100"
              onFocus={(e) => (e.target.style.borderColor = "#FFD900")}
              onBlur={(e) => (e.target.style.borderColor = "#E8E6E3")}
            />
          </div>
          <div>
            <label
              className="block text-xs font-semibold mb-1.5 uppercase tracking-wider"
              style={{ color: "#78736D", letterSpacing: "0.08em" }}
            >
              Número mínimo de posições{" "}
              <span style={{ color: "#B91C1C" }}>*</span>
            </label>
            <input
              {...register("positions")}
              type="number"
              min={1}
              className="w-full h-10 px-3 rounded-xl text-sm outline-none transition-colors"
              style={{ border: "1px solid #E8E6E3", color: "#43403D" }}
              placeholder="Ex: 10"
              onFocus={(e) => (e.target.style.borderColor = "#FFD900")}
              onBlur={(e) => (e.target.style.borderColor = "#E8E6E3")}
            />
            <FieldError message={errors.positions?.message} />
          </div>
        </div>

        {/* Orçamento */}
        <div>
          <label
            className="block text-xs font-semibold mb-1.5 uppercase tracking-wider"
            style={{ color: "#78736D", letterSpacing: "0.08em" }}
          >
            Orçamento (R$) <span style={{ color: "#B91C1C" }}>*</span>
          </label>
          <input
            {...register("budget")}
            type="text"
            className="w-full h-10 px-3 rounded-xl text-sm outline-none transition-colors"
            style={{ border: "1px solid #E8E6E3", color: "#43403D" }}
            placeholder="Ex: 5.000,00"
            onFocus={(e) => (e.target.style.borderColor = "#FFD900")}
            onBlur={(e) => (e.target.style.borderColor = "#E8E6E3")}
          />
          <FieldError message={errors.budget?.message} />
        </div>

        {/* Cidade + Bairros */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label
              className="block text-xs font-semibold mb-1.5 uppercase tracking-wider"
              style={{ color: "#78736D", letterSpacing: "0.08em" }}
            >
              Cidade <span style={{ color: "#B91C1C" }}>*</span>
            </label>
            <Controller
              control={control}
              name="city"
              render={({ field }) => (
                <TicketCityField
                  options={cityOptions}
                  isLoading={citiesLoading}
                  hasError={citiesError}
                  onRetry={retryCities}
                  value={field.value}
                  onChange={(value) =>
                    setValue("city", value, { shouldValidate: true })
                  }
                  onBlur={field.onBlur}
                />
              )}
            />
            <FieldError message={errors.city?.message} />
          </div>
          <div>
            <label
              className="block text-xs font-semibold mb-1.5 uppercase tracking-wider"
              style={{ color: "#78736D", letterSpacing: "0.08em" }}
            >
              Bairro(s)
            </label>
            <input
              {...register("neighborhoodText")}
              type="text"
              className="w-full h-10 px-3 rounded-xl text-sm outline-none transition-colors"
              style={{ border: "1px solid #E8E6E3", color: "#43403D" }}
              placeholder="Ex: Moema, Itaim Bibi"
              onFocus={(e) => (e.target.style.borderColor = "#FFD900")}
              onBlur={(e) => (e.target.style.borderColor = "#E8E6E3")}
            />
            <p className="mt-1 text-xs" style={{ color: "#9E9A94" }}>
              Separe múltiplos bairros por vírgula
            </p>
          </div>
        </div>

        {/* Data de entrada */}
        <div>
          <label
            className="block text-xs font-semibold mb-1.5 uppercase tracking-wider"
            style={{ color: "#78736D", letterSpacing: "0.08em" }}
          >
            Data de entrada prevista <span style={{ color: "#B91C1C" }}>*</span>
          </label>
          <input
            {...register("expectedEntryDate")}
            type="date"
            className="w-full h-10 px-3 rounded-xl text-sm outline-none transition-colors cursor-pointer"
            style={{ border: "1px solid #E8E6E3", color: "#43403D" }}
            onClick={(e) => e.currentTarget.showPicker?.()}
            onFocus={(e) => (e.target.style.borderColor = "#FFD900")}
            onBlur={(e) => (e.target.style.borderColor = "#E8E6E3")}
          />
          <FieldError message={errors.expectedEntryDate?.message} />
        </div>

        {/* Seção personalizado/sob medida */}
        <div
          className="overflow-hidden transition-all duration-300 ease-in-out"
          style={{
            maxHeight: isCustomOffice ? "2000px" : "0px",
            opacity: isCustomOffice ? 1 : 0,
          }}
        >
          <div
            className="flex flex-col gap-5 pl-4"
            style={{ borderLeft: "2px solid #FFD900" }}
          >
            <div>
              <label
                className="block text-xs font-semibold mb-1.5 uppercase tracking-wider"
                style={{ color: "#78736D", letterSpacing: "0.08em" }}
              >
                Tempo de contrato (meses){" "}
                <span style={{ color: "#B91C1C" }}>*</span>
              </label>
              <input
                {...register("contractMonths")}
                type="number"
                className="w-full h-10 px-3 rounded-xl text-sm outline-none transition-colors"
                style={{ border: "1px solid #E8E6E3", color: "#43403D" }}
                placeholder="Ex: 12"
                onFocus={(e) => (e.target.style.borderColor = "#FFD900")}
                onBlur={(e) => (e.target.style.borderColor = "#E8E6E3")}
              />
              <FieldError message={errors.contractMonths?.message} />
            </div>

            <Controller
              control={control}
              name="openToNearby"
              render={({ field }) => (
                <YesNoField
                  label="O cliente está aberto a avaliar opções em bairros vizinhos?"
                  required
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  error={errors.openToNearby?.message}
                />
              )}
            />

            <div>
              <Controller
                control={control}
                name="privateMeetingRooms"
                render={({ field }) => (
                  <YesNoField
                    label="Salas de Reunião Privativas"
                    required
                    error={errors.privateMeetingRooms?.message}
                    value={field.value ?? ""}
                    onChange={(val) => {
                      field.onChange(val);
                      if (val !== "sim") {
                        setValue("meetingRoomCapacities", []);
                      } else {
                        setValue("meetingRoomCapacities", [{ capacity: 0 }]);
                      }
                    }}
                  />
                )}
              />
              {privateMeetingRooms === "sim" && (
                <div className="mt-3 flex flex-col gap-2">
                  <p className="text-xs" style={{ color: "#78736D" }}>
                    Informe a capacidade desejada (em pessoas) para cada sala de
                    reunião.
                  </p>
                  {fields.map((field, i) => (
                    <div key={field.id}>
                      <div className="flex items-center gap-2">
                        <input
                          {...register(`meetingRoomCapacities.${i}.capacity`)}
                          type="number"
                          min={1}
                          className="flex-1 h-9 px-3 rounded-lg text-sm outline-none transition-colors"
                          style={{
                            border: "1px solid #E8E6E3",
                            color: "#43403D",
                            background: "white",
                          }}
                          placeholder="Capacidade (pessoas)"
                          onFocus={(e) =>
                            (e.target.style.borderColor = "#FFD900")
                          }
                          onBlur={(e) =>
                            (e.target.style.borderColor = "#E8E6E3")
                          }
                        />
                        <button
                          type="button"
                          className="w-9 h-9 flex items-center justify-center rounded-lg transition-colors hover:bg-red-50"
                          style={{
                            border: "1px solid #E8E6E3",
                            color: "#78736D",
                          }}
                          onClick={() => remove(i)}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <FieldError
                        message={
                          errors.meetingRoomCapacities?.[i]?.capacity?.message
                        }
                      />
                    </div>
                  ))}
                  <button
                    type="button"
                    className="self-start flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-150"
                    style={{
                      border: "1px solid #E8E6E3",
                      color: "#78736D",
                      background: "transparent",
                    }}
                    onClick={() => append({ capacity: 0 })}
                  >
                    <Plus className="w-3 h-3" />
                    Adicionar sala
                  </button>
                  <FieldError
                    message={
                      errors.meetingRoomCapacities?.root?.message ??
                      errors.meetingRoomCapacities?.message
                    }
                  />
                </div>
              )}
            </div>

            <Controller
              control={control}
              name="willingToAdapt"
              render={({ field }) => (
                <YesNoField
                  label="O cliente está disposto a se adaptar às amenidades oferecidas pelo parceiro/laje?"
                  value={field.value ?? ""}
                  onChange={field.onChange}
                />
              )}
            />

            <div>
              <label
                className="block text-xs font-semibold mb-1.5 uppercase tracking-wider"
                style={{ color: "#78736D", letterSpacing: "0.08em" }}
              >
                Parceiro ou laje específica? Restrições?
              </label>
              <Textarea
                {...register("specificPartner")}
                className="min-h-[80px] rounded-xl text-sm resize-none"
                style={{ borderColor: "#E8E6E3", color: "#43403D" }}
                placeholder="O cliente já indicou algum parceiro ou laje específica? Há restrições de operador?"
              />
            </div>
          </div>
        </div>

        {/* Observações */}
        <div>
          <label
            className="block text-xs font-semibold mb-1.5 uppercase tracking-wider"
            style={{ color: "#78736D", letterSpacing: "0.08em" }}
          >
            Observações
          </label>
          <Textarea
            {...register("observations")}
            className="min-h-[100px] rounded-xl text-sm resize-none"
            style={{ borderColor: "#E8E6E3", color: "#43403D" }}
            placeholder="Informações adicionais relevantes para o ticket..."
          />
        </div>
      </div>
    );
  },
);

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p data-field-error className="mt-1 text-xs" style={{ color: "#B91C1C" }}>
      {message}
    </p>
  );
}

function YesNoField({
  label,
  required,
  value,
  onChange,
  error,
}: {
  label: string;
  required?: boolean;
  value: string;
  onChange: (val: string) => void;
  error?: string;
}) {
  return (
    <div>
      <label
        className="block text-xs font-semibold mb-2 uppercase tracking-wider"
        style={{ color: "#78736D", letterSpacing: "0.08em" }}
      >
        {label} {required && <span style={{ color: "#B91C1C" }}>*</span>}
      </label>
      <div className="flex gap-2">
        <button
          type="button"
          className="px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-150"
          style={{
            background: value === "sim" ? "#1A1814" : "transparent",
            color: value === "sim" ? "#FFD900" : "#78736D",
            border: `1px solid ${value === "sim" ? "#1A1814" : "#E8E6E3"}`,
          }}
          onClick={() => onChange("sim")}
        >
          Sim
        </button>
        <button
          type="button"
          className="px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-150"
          style={{
            background: value === "nao" ? "#1A1814" : "transparent",
            color: value === "nao" ? "#FFD900" : "#78736D",
            border: `1px solid ${value === "nao" ? "#1A1814" : "#E8E6E3"}`,
          }}
          onClick={() => onChange("nao")}
        >
          Não
        </button>
      </div>
      <FieldError message={error} />
    </div>
  );
}
